import { useEffect } from 'react'

import { FeatureFlagName } from '@api'
import { useWindowSize } from '@uidotdev/usehooks'
import clsx from 'clsx'
import { useUnit } from 'effector-react'
import userflow from 'userflow.js'

import CloseIcon from '@/assets/icons/icon-close.svg'
import LinkIcon from '@/assets/icons/icon-external.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import QuestionIcon from '@/assets/icons/icon-question.svg'
import { $account } from '@/models/account.ts'
import { $currentCreator } from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags.ts'
import { useDesktopApp } from '@/modules/desktop/DesktopAppProvider.tsx'
import { USERFLOW_SECRET } from '@/utils/constants.ts'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags.ts'
import { useIsDarkTheme } from '@/utils/useTheme.tsx'

import { IconButton } from '../chatting/ui/components/icon-button'
import './styles.css'
import { usePageGuide } from './usePageGuide'

export const PageInfo = () => {
  const activeCreator = useUnit($currentCreator)
  const { articleRef, title, body, onToggle, isOpen, onClose, onOpenFull } =
    usePageGuide()
  const account = useUnit($account)
  const isDarkMode = useIsDarkTheme()
  const { isApp } = useDesktopApp()
  const { width } = useWindowSize()
  const isMobile = (width || 0) < 640
  const availableFeatures = useUnit($availableFeatures)

  const isUserFlowFeatureAvailable =
    isFeatureAvailableUtil(availableFeatures, FeatureFlagName.USER_FLOW) &&
    // for dev
    !!USERFLOW_SECRET

  useEffect(() => {
    if (
      !account ||
      !isUserFlowFeatureAvailable ||
      !availableFeatures?.length ||
      !USERFLOW_SECRET
    ) {
      return
    }

    userflow.init(USERFLOW_SECRET)

    userflow.identify(account.userId.toString(), {
      name: account.name.toString(),
      plan: activeCreator ? activeCreator.plan : 'No licence',
      accountRole: account.role,
      inTrial: account.inTrial,
      email: account.email,
      memberRole: account.memberRole,
      theme: isDarkMode ? 'dark' : 'light',
      isDesktopApp: isApp,
      isMobileWidth: isMobile,
    })
  }, [
    account,
    isDarkMode,
    isApp,
    activeCreator,
    isMobile,
    isUserFlowFeatureAvailable,
    availableFeatures,
  ])

  if (!title || isUserFlowFeatureAvailable) {
    return null
  }

  return (
    <>
      <div
        className={clsx(
          'fixed z-100 rounded-lg bottom-0 right-0 bg-white dark:bg-secondary-700 shadow-lg transition-all duration-300 ease-in-out box-border overflow-hidden md:right-[78px] md:bottom-[80px]',
          isOpen
            ? 'w-full h-full opacity-100 md:w-[465px] md:h-[600px]'
            : 'w-0 h-0 opacity-0',
        )}
      >
        <div className="flex items-center justify-between h-[60px] px-4">
          <div className="flex items-center gap-2">
            <h2 className="text-title text-[20px] font-semibold">{title}</h2>
            <IconButton
              onClick={onOpenFull}
              icon={LinkIcon}
              tooltip="Full Guide"
              className="hidden md:inline-flex"
            />
          </div>
          <IconButton
            onClick={onClose}
            icon={CloseIcon}
            iconClassName="h-[20px] w-[20px]"
            tooltip="Close"
          />
        </div>
        {body ? (
          <div
            ref={articleRef}
            className="text-title text-[12px] article-content overflow-y-auto h-[calc(100%-60px)] px-4 pb-4"
            dangerouslySetInnerHTML={{ __html: body || '' }}
          />
        ) : (
          <div className="flex items-center justify-center h-[calc(100%-60px)] text-title text-[14px]">
            <IconLoading className="animate-spin h-8 w-8 mr-2" />
          </div>
        )}
      </div>
      <IconButton
        className="fixed z-10 bottom-[78px] right-[20px] w-[48px] h-[48px] flex items-center justify-center bg-white dark:bg-secondary-700 shadow-lg rounded-lg"
        onClick={onToggle}
        icon={isOpen ? CloseIcon : QuestionIcon}
        iconClassName="h-[20px] w-[20px] text-title"
        tooltip={isOpen ? 'Close Guide' : title}
      />
    </>
  )
}
